import { VALIDATOR_TYPES, isValidInteger } from "Validation/validator";

export const WEB_ADAPTER_TYPES = {
    custom: 1,
    report: 2,
    standard: 3,
    customListLookup: 4,
    projectLookup: 5,
    lambdaCustom: 6
};

export const PROPERTY_TYPES = {
    readWrite: 0,
    readOnly: 1,
    hidden: 2
};

export const CUSTOM_WEB_ADAPTER_ACTIVATION = {
    inactive: 0,
    active: 1,
    disabled: 2
};

export const CONNECTION_PAYLOAD_TYPES = {
    json: "JSON",
    xml: "XML"
};

export const CONNECTION_FIELD_TYPES = {
    string: "String",
    choice: "Choice",
    dateTime: "DateTime",
    integer: "Int64",
    decimal: "Decimal",
    boolean: "Boolean",
    attachment: "Attachment",
    user: "User",
    builtIn: "BuiltIn",
    locationLatitude: "LocationLatitude",
    locationLongitude: "LocationLongitude",
    stringTableColumn: "StringTableColumn",
    integerTableColumn: "Int64TableColumn",
    decimalTableColumn: "DecimalTableColumn",
    dateTimeTableColumn: "DateTimeTableColumn"
};

export const CONNECTION_FIELD_LOCATION = {
    queryParameters: "RequestQueryParameter",
    payload: "RequestPayloadField"
};

export const ERROR_MESSAGE_SOURCE_CONSTANTS = {
    StaticErrorMessage: "StaticErrorMessage",
    ErrorMessageField: "ErrorMessageField"
};

export const PROPERTY_SPECIAL_VALIDATION = {
    none: 0,
    email: 1,
    httpsOnly: 2,
    reservedHeaders: 3
};

export const CONNECTION_HTTP_METHODS = {
    POST: "POST",
    PUT: "PUT",
    GET: "GET",
    DELETE: "DELETE",
    PATCH: "PATCH"
};

export const CONNECTION_HTTP_STATUS_CODES = {
    success: 1,
    badRequest: 2,
    unauthorized: 3,
    notFound: 4,
    internalServerError: 5,
    timeout: 6,
    fourxx: 7,
    fivexx: 8
};

export function getTextForHttpStatusCodeInteger(statusCodeInt) {
    switch (statusCodeInt) {
        case CONNECTION_HTTP_STATUS_CODES.success:
            return "200";
        case CONNECTION_HTTP_STATUS_CODES.badRequest:
            return "400";
        case CONNECTION_HTTP_STATUS_CODES.unauthorized:
            return "401";
        case CONNECTION_HTTP_STATUS_CODES.notFound:
            return "404";
        case CONNECTION_HTTP_STATUS_CODES.internalServerError:
            return "500";
        case CONNECTION_HTTP_STATUS_CODES.timeout:
            return "Timeout";
        case CONNECTION_HTTP_STATUS_CODES.fourxx:
            return "4XX";
        case CONNECTION_HTTP_STATUS_CODES.fivexx:
            return "5XX";
        default:
            return null;
    }
}

export const CONNECTION_DIALOG_RESULT = {
    okResult: "OK",
    cancelResult: "CANCEL"
};

export function getValidatorTypesForFieldType(fieldType) {
    switch (fieldType) {
        case CONNECTION_FIELD_TYPES.dateTime:
            return [VALIDATOR_TYPES.date];
        case CONNECTION_FIELD_TYPES.integer:
            return [VALIDATOR_TYPES.integer];
        case CONNECTION_FIELD_TYPES.decimal:
            return [VALIDATOR_TYPES.decimal];
        case CONNECTION_FIELD_TYPES.locationLatitude:
            return [
                {
                    type: VALIDATOR_TYPES.decimal
                },
                {
                    type: VALIDATOR_TYPES.max,
                    max: 90
                },
                {
                    type: VALIDATOR_TYPES.min,
                    min: -90
                }
            ];
        case CONNECTION_FIELD_TYPES.locationLongitude:
            return [
                {
                    type: VALIDATOR_TYPES.decimal
                },
                {
                    type: VALIDATOR_TYPES.max,
                    max: 180
                },
                {
                    type: VALIDATOR_TYPES.min,
                    min: -180
                }
            ];
        case CONNECTION_FIELD_TYPES.boolean:
            return [VALIDATOR_TYPES.boolean];
        default:
            return [];
    }
}

export function parseVisibilityCondition(visibilityCondition) {
    if (visibilityCondition) {
        const parts = visibilityCondition.split("=");
        const propName = parts[0];
        // Rejoin parts after the first = in case = is part of the value
        const propValueStr = parts.slice(1).join("=");
        const propValues = [];
        let curValue = "";
        for (const cur of propValueStr.split(",")) {
            curValue = cur;
            // If necessary later, add escaping code if there is a case where a comma is needed
            propValues.push(curValue);
            curValue = "";
        }
        return { propName, propValues };
    } else {
        return null;
    }
}

export function splitKeyPathString(keyPath) {
    if (!keyPath) {
        return null;
    }

    const segments = [];
    let segmentStart = 0;
    if (keyPath.charAt(0) === KEY_PATH_SEGMENT_SEPARATOR) {
        segmentStart = 1;
    }

    while (segmentStart < keyPath.length) {
        let segmentEnd = keyPath.indexOf(KEY_PATH_SEGMENT_SEPARATOR, segmentStart);

        // Skip escaped slashes
        while (
            segmentEnd != -1 &&
            keyPath[segmentEnd - 1] === KEY_PATH_ESCAPE &&
            (segmentEnd < 2 || keyPath[segmentEnd - 2] !== KEY_PATH_ESCAPE)
        ) {
            segmentEnd = keyPath.indexOf(KEY_PATH_SEGMENT_SEPARATOR, segmentEnd + 1);
        }

        if (segmentEnd === -1) {
            segments.push(keyPath.substring(segmentStart));
            break;
        }

        segments.push(keyPath.substring(segmentStart, segmentEnd));
        segmentStart = segmentEnd + 1;
    }

    return segments;
}

export function isArraySegment(segment) {
    return segment === KEY_PATH_ARRAY;
}

export function isArrayIndexSegment(segment) {
    return isValidInteger(segment) && Number(segment) >= 0;
}

export function areKeyPathSegmentEscapeCharactersValid(keyPath) {
    if (!keyPath) {
        return true;
    }

    let escapeCharacterIndex = keyPath.indexOf(KEY_PATH_ESCAPE);
    while (escapeCharacterIndex !== -1) {
        // Check for trailing escape character
        if (escapeCharacterIndex === keyPath.length - 1) {
            return false;
        }

        const escapedCharacter = keyPath.charAt(escapeCharacterIndex + 1);
        if (escapedCharacter != KEY_PATH_ESCAPE && escapedCharacter != KEY_PATH_SEGMENT_SEPARATOR) {
            return false;
        }

        escapeCharacterIndex = keyPath.indexOf(KEY_PATH_ESCAPE, escapeCharacterIndex + 2);
    }

    return true;
}

export function areKeyPathSegmentsNonEmpty(keyPath) {
    if (!keyPath) {
        return true;
    }

    const segments = splitKeyPathString(keyPath);
    for (const segment of segments) {
        if (!segment.trim()) {
            return false;
        }
    }

    return true;
}

export function doesKeyPathStartWithKey(keyPath) {
    if (!keyPath) {
        return true;
    }

    const segments = splitKeyPathString(keyPath);
    return segments.length > 0 && !isArraySegment(segments[0]) && !isArrayIndexSegment(segments[0]);
}

export function doesKeyPathOnlyReferenceKeysAndIndices(keyPath) {
    if (!keyPath) {
        return true;
    }

    const segments = splitKeyPathString(keyPath);
    for (const segment of segments) {
        if (isArraySegment(segment)) {
            return false;
        }
    }

    return true;
}

export function areKeyPathIndicesValid(keyPath) {
    if (!keyPath) {
        return true;
    }

    const segments = splitKeyPathString(keyPath);
    for (const segment of segments) {
        if (isArrayIndexSegment(segment) && Number(segment) > KEY_PATH_MAX_INDEX) {
            return false;
        }
    }

    return true;
}

export function isKeyPathNestingValid(keyPath) {
    if (!keyPath) {
        return true;
    }

    const segments = splitKeyPathString(keyPath);
    return segments.length <= KEY_PATH_MAX_NESTING;
}

export const NAME_MAX_LENGTH = 256;
export const CONFIG_NAME_MAX_LENGTH = 128;
export const DYNAMIC_FIELD_NAME_MAX_LENGTH = 128;
export const RELATIVE_URI_MAX_LENGTH = 2048;
export const KEY_PATH_MAX_LENGTH = 512;
export const KEY_PATH_MAX_INDEX = 255;

export const KEY_PATH_SEGMENT_SEPARATOR = "/";
export const KEY_PATH_ESCAPE = "~";
export const KEY_PATH_ARRAY = "*";
export const KEY_PATH_MAX_NESTING = 20;
